<template>
    <div>
        <heading-title>We are required to check that you are eligible for investments you make with us and that these investments are reasonably suitable for your situation. Please complete the following to help us make these determinations regarding your eligibility and suitability.</heading-title>

        <div class="space-y-4">
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div class="col-span-1">
                    <field-value title="Annual Income">{{ convertValue(data.annualIncome) }}</field-value>
                </div>

                <div class="col-span-1">
                    <field-value title="Net Worth">{{ convertValue(data.netWorth) }}</field-value>
                </div>

                <div v-show="data.annualIncome == '200k_300k'" class="col-span-1">
                    <field-value title="Primary Income">{{ convertValue(data.primaryIncome) }}</field-value>
                </div>

                <div v-show="data.annualIncome == '200k_300k'" class="col-span-1">
                    <field-value title="Spouse Income">{{ convertValue(data.spouseIncome) }}</field-value>
                </div>

                <div class="col-span-1">
                    <field-value title="Liquid Net Worth">{{ convertValue(data.liquidNetWorth) }}</field-value>
                </div>

                <div class="col-span-1">
                    <field-value title="Tax Rate">{{ convertValue(data.taxRate) }}</field-value>
                </div>

                <div class="col-span-1 sm:col-span-2">
                    <heading-title class="!mb-0">This investment represents the following portion of my financial portfolio:</heading-title>
                </div>

                <div class="col-span-1 sm:col-span-2">
                    <field-value>{{ convertValue(data.financialPortfolio) }}</field-value>
                </div>

                <div class="col-span-1 sm:col-span-2">
                    <heading-title class="!mb-0">Source of Funds</heading-title>
                </div>

                <div class="col-span-1 sm:col-span-2">
                    <field-value title="What is the source of the funds you are using for this investment?">{{ convertValue(data.sourceFunds) }}</field-value>
                </div>

                <div class="col-span-1 sm:col-span-2">
                    <heading-title class="!mb-0">Please describe your prior experience with private investments:</heading-title>
                </div>

                <div class="col-span-1 sm:col-span-2">
                    <field-value>{{ convertValue(data.priorExperience) }}</field-value>
                </div>

                <div class="col-span-1 sm:col-span-2">
                    <heading-title class="!mb-0">Preferred Advisor Partner Code:</heading-title>
                </div>

                <div class="col-span-1 sm:col-span-2">
                    <field-value>{{ convertValue(data.partnerCode) }}</field-value>
                </div>

                <div class="col-span-1 sm:col-span-2">
                    <heading-title class="!mb-0">Do you have a financial advisor or CPA you want us to share information with?</heading-title>
                </div>

                <div class="col-span-1 sm:col-span-2">
                    <field-value>{{ convertValue(data.financialAdvisor) }}</field-value>
                </div>

                <div class="col-span-1 sm:col-span-2">
                    <heading-title class="!mb-0">Do you have a financial advisor or CPA you want us to share information with?</heading-title>
                </div>

                <div class="col-span-1 sm:col-span-2">
                    <field-value>{{ convertValue(data.financialAdvisor) }}</field-value>
                </div>

                <template v-if="data.financialAdvisor == 'Yes'">
                    <div class="col-span-1 sm:col-span-2">
                        <heading-title class="!mb-0">Financial Advisor/CPA Information</heading-title>
                    </div>

                    <div class="col-span-1">
                        <field-value title="First Name">{{ convertValue(data.advisorFirstName) }}</field-value>
                    </div>

                    <div class="col-span-1">
                        <field-value title="Last Name">{{ convertValue(data.advisorLastName) }}</field-value>
                    </div>

                    <div class="col-span-1">
                        <field-value title="Firm Name">{{ convertValue(data.advisorFirm) }}</field-value>
                    </div>

                    <div class="col-span-1">
                        <field-value title="Email Address">{{ convertValue(data.advisorEmail) }}</field-value>
                    </div>

                    <div class="col-span-1">
                        <field-value title="Phone Number">{{ convertValue(data.advisorPhone) }}</field-value>
                    </div>
                </template>

                <div class="col-span-1 sm:col-span-2">
                    <heading-title class="!mb-0">Trusted Contact</heading-title>
                </div>

                <div class="col-span-1 sm:col-span-2">
                    <field-value title="I have a trusted contact that I would like to have contacted in event the issuer, placement agent, registered representative, or affiliates have questions or concerns about my whereabouts or health status; suspect that I may be a victim of fraud or financial exploitation; suspect that I might no longer be able to handle my financial affairs; to confirm identity of any legal guardian, executor, trustee, authorized party, or power attorney; has any other concerns; or is unable to contact me about my investment.">{{ convertValue(data.trustedContact) }}</field-value>
                </div>

                <template v-if="data.trustedContact == 'Yes'">
                    <div class="col-span-1 sm:col-span-2">
                        <p class="pt-4 text-lg font-medium text-gray-500"><span class="text-gray-700">Note:</span> Your trusted contact must be someone other than the investment owner.</p>
                    </div>

                    <div class="col-span-1">
                        <field-value title="First Name">{{ convertValue(data.contactFirstName) }}</field-value>
                    </div>

                    <div class="col-span-1">
                        <field-value title="Last Name">{{ convertValue(data.contactLastName) }}</field-value>
                    </div>

                    <div class="col-span-1">
                        <field-value title="Phone Number">{{ convertValue(data.contactPhone) }}</field-value>
                    </div>

                    <div class="col-span-1">
                        <field-value title="Email Address">{{ convertValue(data.contactEmail) }}</field-value>
                    </div>

                    <div class="col-span-1">
                        <field-value title="Relationship">{{ convertValue(data.contactRelation) }}</field-value>
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
import HeadingTitle from '@Components/HeadingTitle.vue'
import FieldValue from '@Components/FieldValue.vue'
import { convertIntakeValue } from '@/utils'

export default {
    components: {
        HeadingTitle,
        FieldValue
    },
    props: {
        data: {
            type: Object,
            required: true
        }
    },
    setup(props){
        function convertValue(value){
            return (value) ? convertIntakeValue(value) : '-'
        }

        return {
            convertValue
        }
    }
}
</script>

<style>

</style>
